import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, Database, History, AlertTriangle, CheckCircle, 
  Download, RefreshCw, Loader2, FileText, Clock, User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const Card = ({ children, className = "" }) => (
  <div className={`bg-cream-100 rounded-2xl border border-cream-300 p-6 ${className}`}>
    {children}
  </div>
);

/**
 * Admin Data Protection Dashboard
 * 
 * Provides visibility into:
 * - Audit logs for all critical operations
 * - Progress snapshots (automatic backups)
 * - Data integrity checks
 * - Manual backup creation
 */
const AdminDataProtection = ({ Layout, getAuthHeader }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [integrityStatus, setIntegrityStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [logsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/audit-logs?limit=50`, getAuthHeader()),
        axios.get(`${API}/admin/users`, getAuthHeader())
      ]);
      setAuditLogs(logsRes.data.logs || []);
      setUsers(usersRes.data.filter(u => u.role !== 'admin'));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    }
    setLoading(false);
  };

  const loadUserSnapshots = async (userId, courseId = null) => {
    setLoadingSnapshots(true);
    try {
      const url = courseId 
        ? `${API}/admin/progress-snapshots/${userId}?course_id=${courseId}`
        : `${API}/admin/progress-snapshots/${userId}`;
      const res = await axios.get(url, getAuthHeader());
      setSnapshots(res.data.snapshots || []);
    } catch (error) {
      console.error('Error loading snapshots:', error);
      toast.error('Fehler beim Laden der Snapshots');
    }
    setLoadingSnapshots(false);
  };

  const checkIntegrity = async (userId, courseId) => {
    try {
      const res = await axios.get(
        `${API}/admin/data-integrity/${userId}/${courseId}`,
        getAuthHeader()
      );
      setIntegrityStatus(res.data);
      if (res.data.status === 'OK') {
        toast.success('Datenintegrität OK - Keine Probleme gefunden');
      } else {
        toast.warning(`${res.data.issues_found} Probleme gefunden`);
      }
    } catch (error) {
      console.error('Error checking integrity:', error);
      toast.error('Fehler bei der Integritätsprüfung');
    }
  };

  const createBackup = async (userId, courseId) => {
    setCreatingBackup(true);
    try {
      const res = await axios.post(
        `${API}/admin/create-backup/${userId}/${courseId}`,
        {},
        getAuthHeader()
      );
      toast.success('Backup erfolgreich erstellt!');
      loadUserSnapshots(userId, courseId);
      loadData(); // Refresh audit logs
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Fehler beim Erstellen des Backups');
    }
    setCreatingBackup(false);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('de-DE');
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'APPROVE_SESSION': return 'text-green-400';
      case 'REVOKE_SESSION': return 'text-red-400';
      case 'MANUAL_BACKUP': return 'text-blue-400';
      default: return 'text-sand-500';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-terra" />
            <h1 className="font-display text-4xl text-sand-800 tracking-wide uppercase">Datensicherheit</h1>
          </div>
          <p className="text-sand-500">Audit-Logs, Backups und Datenintegrität</p>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cream-200 flex items-center justify-center">
                <History className="w-5 h-5 text-terra" />
              </div>
              <div>
                <p className="text-xl font-bold text-sand-800">{auditLogs.length}</p>
                <p className="text-xs text-sand-500">Audit-Einträge</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cream-200 flex items-center justify-center">
                <Database className="w-5 h-5 text-terra" />
              </div>
              <div>
                <p className="text-xl font-bold text-sand-800">{snapshots.length}</p>
                <p className="text-xs text-sand-500">Snapshots (gewählter User)</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cream-200 flex items-center justify-center">
                {integrityStatus?.status === 'OK' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : integrityStatus?.status === 'ISSUES_FOUND' ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                ) : (
                  <Shield className="w-5 h-5 text-sand-500" />
                )}
              </div>
              <div>
                <p className="text-xl font-bold text-sand-800">
                  {integrityStatus ? (integrityStatus.status === 'OK' ? 'OK' : integrityStatus.issues_found) : '-'}
                </p>
                <p className="text-xs text-sand-500">Integrität</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* User Selection & Actions */}
          <Card>
            <h2 className="text-lg font-semibold text-sand-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Teilnehmer-Daten prüfen
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-sand-500 mb-2">Teilnehmer auswählen</label>
                <select
                  value={selectedUser?.id || ''}
                  onChange={(e) => {
                    const u = users.find(x => x.id === e.target.value);
                    setSelectedUser(u);
                    if (u) loadUserSnapshots(u.id);
                  }}
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                >
                  <option value="">-- Bitte wählen --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} (@{u.username})</option>
                  ))}
                </select>
              </div>

              {selectedUser && (
                <>
                  <div className="p-4 bg-cream-200 rounded-xl">
                    <p className="text-sm text-sand-500 mb-1">Gewählt:</p>
                    <p className="font-medium text-sand-800">{selectedUser.name}</p>
                    <p className="text-xs text-sand-500">@{selectedUser.username}</p>
                    {selectedUser.assigned_courses?.length > 0 && (
                      <p className="text-xs text-sand-500 mt-2">
                        {selectedUser.assigned_courses.length} Kurse zugewiesen
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => checkIntegrity(selectedUser.id, 'course-pdl-weiterbildung-teil1')}
                      className="flex items-center gap-2 px-4 py-2 bg-cream-300 rounded-lg text-sand-800 hover:bg-cream-300 transition-colors text-sm"
                    >
                      <Shield className="w-4 h-4" />
                      Integrität prüfen
                    </button>
                    <button
                      onClick={() => createBackup(selectedUser.id, 'course-pdl-weiterbildung-teil1')}
                      disabled={creatingBackup}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-terra to-terra-dark rounded-lg text-sand-800 font-medium hover:opacity-90 transition-opacity text-sm disabled:opacity-50"
                    >
                      {creatingBackup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                      Backup erstellen
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Snapshots for selected user */}
            {selectedUser && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-sand-500 mb-3">
                  Snapshots für {selectedUser.name}
                </h3>
                {loadingSnapshots ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-terra" />
                  </div>
                ) : snapshots.length === 0 ? (
                  <p className="text-sm text-sand-500 py-4">Keine Snapshots vorhanden</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {snapshots.slice(0, 10).map((s, idx) => (
                      <div key={idx} className="p-3 bg-cream-200 rounded-lg text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-sand-800">{formatDate(s.created_at)}</span>
                          <span className="text-xs px-2 py-1 bg-cream-300 rounded text-sand-500">
                            {s.trigger}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Audit Logs */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-sand-800 flex items-center gap-2">
                <History className="w-5 h-5" />
                Audit-Log
              </h2>
              <button
                onClick={loadData}
                className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300 transition-colors"
              >
                <RefreshCw className="w-4 h-4 text-sand-500" />
              </button>
            </div>

            {auditLogs.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-10 h-10 mx-auto text-[#2A2A2A] mb-3" />
                <p className="text-sand-500">Noch keine Audit-Einträge</p>
                <p className="text-xs text-sand-400 mt-1">
                  Einträge erscheinen bei Session-Genehmigungen, Backups etc.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="p-3 bg-cream-200 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="text-xs text-sand-500">{formatDate(log.timestamp)}</span>
                    </div>
                    <p className="text-xs text-sand-500">{log.details}</p>
                    <p className="text-xs text-sand-400 mt-1">von {log.performed_by_name}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Integrity Issues */}
        {integrityStatus?.issues?.length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Gefundene Probleme
            </h2>
            <div className="space-y-2">
              {integrityStatus.issues.map((issue, idx) => (
                <div key={idx} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm font-medium text-yellow-400">{issue.module_id}</p>
                  <p className="text-xs text-sand-500">{issue.details}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Info Banner */}
        <div className="mt-6 p-4 bg-cream-200 rounded-xl border border-cream-300">
          <h3 className="text-sm font-medium text-terra mb-2">ℹ️ Sicherheitshinweise</h3>
          <ul className="text-xs text-sand-500 space-y-1">
            <li>• <strong>Lernzeit kann nur steigen</strong> - nie sinken (Schutz vor Datenverlust)</li>
            <li>• <strong>Abgeschlossene Module sind gesperrt</strong> - nach Live-Session-Genehmigung unveränderbar</li>
            <li>• <strong>Automatische Snapshots</strong> werden vor jeder kritischen Aktion erstellt</li>
            <li>• <strong>Alle Admin-Aktionen</strong> werden im Audit-Log protokolliert</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDataProtection;
